import { useQuery, useMutation, useQueryClient, type UseQueryResult } from '@tanstack/react-query';
import { applicationApi } from '../api/endpoints/application';
import { generateIdempotencyKey } from '../lib/idempotency';
import type {
    ApplicationPermissionLocal,
    UpsertApplicationPermissionRequestLocal,
} from '../api/types/ApplicationLocal';

/**
 * Hooks TanStack Query del catálogo de permisos de una aplicación
 * (`/applications/{applicationId}/permissions`). queryKey
 * `['applications', applicationId, 'permissions']`. Las mutations invalidan ese catálogo.
 *
 * El orden de presentación (alfabético por `displayName`/`permissionKey`, default del proyecto) lo
 * resuelve el consumidor de UI — acá se devuelve la lista tal cual la sirve el backend.
 */

function permissionsKey(applicationId: string | undefined) {
    return ['applications', applicationId, 'permissions'] as const;
}

export function useApplicationPermissions(
    applicationId: string | undefined,
): UseQueryResult<ApplicationPermissionLocal[]> {
    return useQuery({
        queryKey: permissionsKey(applicationId),
        queryFn: () => applicationApi.listPermissions(applicationId ?? ''),
        enabled: !!applicationId,
        select: (r) => r.items,
    });
}

export function useCreateApplicationPermission(applicationId: string) {
    const qc = useQueryClient();
    return useMutation({
        mutationFn: (body: UpsertApplicationPermissionRequestLocal) =>
            applicationApi.createPermission(applicationId, body, {
                idempotencyKey: generateIdempotencyKey(),
            }),
        onSuccess: async () => {
            await qc.invalidateQueries({ queryKey: permissionsKey(applicationId) });
        },
    });
}

export function useUpdateApplicationPermission(applicationId: string) {
    const qc = useQueryClient();
    return useMutation({
        mutationFn: ({
            permissionKey,
            body,
        }: {
            permissionKey: string;
            body: UpsertApplicationPermissionRequestLocal;
        }) => applicationApi.updatePermission(applicationId, permissionKey, body),
        onSuccess: async () => {
            await qc.invalidateQueries({ queryKey: permissionsKey(applicationId) });
        },
    });
}

export function useDeleteApplicationPermission(applicationId: string) {
    const qc = useQueryClient();
    return useMutation({
        mutationFn: (permissionKey: string) =>
            applicationApi.deletePermission(applicationId, permissionKey),
        onSuccess: async () => {
            await qc.invalidateQueries({ queryKey: permissionsKey(applicationId) });
        },
    });
}
